import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

/**
 * Input (TextField) — single-line text entry.
 * Figma: Design System / Compositions / TextField (242:712).
 * Visual state (default/error) is driven by the `error` prop or
 * `aria-invalid="true"`; focus and disabled are handled by CSS pseudo-classes.
 */

const inputVariants = cva(
    [
        'flex w-full rounded-md border bg-[var(--bg-surface)] text-[var(--text-primary)] transition-colors duration-200',
        'placeholder:text-[var(--text-tertiary)]',
        'focus:outline-none focus:ring-2',
        'disabled:cursor-not-allowed disabled:bg-[var(--bg-subtle)] disabled:border-[var(--border-subtle)] disabled:text-[var(--text-disabled)] disabled:placeholder:text-[var(--text-disabled)]',
    ],
    {
        variants: {
            inputSize: {
                sm: 'h-8 px-3 text-sm',
                md: 'h-10 px-3 py-2 text-sm',
            },
            state: {
                default:
                    'border-[var(--border-input)] focus:border-[var(--border-focus)] focus:ring-[var(--primary-200)]',
                error:
                    'border-[var(--border-danger)] focus:border-[var(--border-danger)] focus:ring-[var(--danger-100)]',
            },
        },
        defaultVariants: {
            inputSize: 'md',
            state: 'default',
        },
    },
)

export interface InputProps
    extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>,
        Omit<VariantProps<typeof inputVariants>, 'state'> {
    error?: boolean
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
    ({ className, type, error, inputSize, 'aria-invalid': ariaInvalid, ...props }, ref) => {
        const isInvalid = Boolean(
            error || ariaInvalid === true || ariaInvalid === 'true',
        )
        return (
            <input
                type={type}
                aria-invalid={isInvalid || undefined}
                className={cn(
                    inputVariants({ inputSize, state: isInvalid ? 'error' : 'default' }),
                    className,
                )}
                ref={ref}
                {...props}
            />
        )
    },
)
Input.displayName = 'Input'

export { Input, inputVariants }
